import React, { useState, useCallback, useContext, useEffect } from "react"
import PropTypes from "prop-types"
import styled from "styled-components"
import {
  getCountries,
  getCountryCallingCode,
} from "react-phone-number-input/input"
import Flags from "react-phone-number-input/flags"
import en from "react-phone-number-input/locale/en.json"
import InputDropdown from "@src/components/input-dropdown"
import { LocaleContext } from "@src/context/locale-context"
const StyledDropdown = styled(InputDropdown)`
  & .country-flag {
    width: 32px;
  }
  & .select-button {
    width: 100%;
  }
  & ul {
    min-width: 350px;
  }
  & li,
  & .select-button {
    display: flex;
    align-items: center;
    & .country-flag {
      margin-right: 10px;
      flex: 0 0 auto;
    }
  }
  & li.pinned-country + li:not(.pinned-country) {
    border-top: 1px solid ${props => props.theme.borderGray};
  }
`

const countryList = getCountries()
const defaultPinnedCountries = ["CA", "US", "GB"]

const InputCountry = ({
  className,
  onChange,
  renderSelectButton,
  withCallingCodes,
  pinnedCountries: propPinnedCountries,
  ...props
}) => {
  const [countryOptions, setCountryOptions] = useState()
  const [country, setCountry] = useState()
  const { country: localeCountry } = useContext(LocaleContext)
  const pinnedCountries = propPinnedCountries || defaultPinnedCountries

  const handleCountryChange = useCallback(
    value => {
      setCountry(value)
      if (onChange) {
        onChange(value)
      }
    },
    [setCountry, onChange]
  )

  useEffect(() => {
    if (
      localeCountry &&
      countryOptions &&
      (!country || (country.isLocale && country.value !== localeCountry))
    ) {
      const localeValue = countryOptions.find(
        item => item.value === localeCountry
      )
      handleCountryChange(
        localeValue ? { ...localeValue, isLocale: true } : undefined
      )
    }
  }, [localeCountry, country, handleCountryChange, countryOptions])

  useEffect(() => {
    setCountryOptions(
      countryList
        .map(country => {
          const countryOption = {
            text:
              `${en[country]}` +
              (withCallingCodes ? ` +${getCountryCallingCode(country)}` : ""),
            value: country,
            icon: React.createElement(Flags[country], {
              title: en[country],
              className: "country-flag",
            }),
          }
          if (pinnedCountries.includes(country)) {
            countryOption.className = "pinned-country"
          }
          return countryOption
        })
        .sort((a, b) => {
          if (pinnedCountries) {
            if (
              pinnedCountries.includes(a.value) &&
              !pinnedCountries.includes(b.value)
            ) {
              return -1
            } else if (
              pinnedCountries.includes(b.value) &&
              !pinnedCountries.includes(a.value)
            ) {
              return 1
            }
          }
          return `${en[a.value]}`.localeCompare(`${en[b.value]}`)
        })
    )
  }, [setCountryOptions, withCallingCodes, pinnedCountries])

  const renderDropdownSelectButton = useCallback(
    item => {
      if (renderSelectButton) {
        return renderSelectButton(item)
      }
      return (
        item && (
          <>
            {item.icon}
            <span>{item.text}</span>
          </>
        )
      )
    },
    [renderSelectButton]
  )

  return (
    <StyledDropdown
      className={className}
      value={country}
      onChange={handleCountryChange}
      options={countryOptions}
      renderSelectButton={renderDropdownSelectButton}
      {...props}
    />
  )
}
InputCountry.propTypes = {
  className: PropTypes.string,
  onChange: PropTypes.func,
  renderSelectButton: PropTypes.func,
  withCallingCodes: PropTypes.bool,
  pinnedCountries: PropTypes.arrayOf(PropTypes.string),
}
export default InputCountry
